"use client"

import { Head } from "@inertiajs/react"
import { SearchLg } from "@untitledui/icons"
import { AnimatePresence, motion } from "motion/react"
import { useMemo, useState } from "react"

import { CollabCard } from "@/components/collabs"
import { HighlighterStroke } from "@/components/ui/highlighter-stroke"
import { Input } from "@/components/ui/input"
import { AuthedShell } from "@/layouts/authed-shell"
import type { Brief } from "@/types"

interface CollabsIndexProps {
  briefs: Brief[]
}

export default function CollabsIndex({ briefs }: CollabsIndexProps) {
  const [query, setQuery] = useState("")

  /*
   * The filteredBriefs() function manages briefs to be displayed.
   * Returns briefs filtered by the search query if it exists.
   * Otherwise, falls back to the complete list provided via props.
   */
  const filteredBriefs = useMemo(() => {
    const keyword = query.trim().toLowerCase()
    if (!keyword) return briefs
    return briefs.filter((brief) => brief.title.toLowerCase().includes(keyword))
  }, [briefs, query])

  return (
    <AuthedShell headerVariant="light">
      <Head title="Community Playlist Briefs" />

      {/* Inverted navy masthead: the section flips onto surface-strong so the
          page opens like a print masthead, with the type, Scotch rule, and
          details redrawn in paper white to contrast the navy ground. */}
      <section className="bg-surface-strong border-b border-white/15 py-16 lg:py-24">
        <div className="max-w-container mx-auto flex flex-col gap-5 px-4 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-2">
              <span
                aria-hidden="true"
                className="scotch-rule scotch-rule-action mb-2"
              />
              <h1 className="text-primary_on-brand text-display-sm lg:text-display-md font-semibold text-balance">
                Community Playlist Briefs
              </h1>
              <p className="text-secondary_on-brand text-lg text-pretty">
                Each brief is an open call. Browse what the community is
                building,{" "}
                <HighlighterStroke onStrong>add your voice</HighlighterStroke>,
                and shape playlists for the people who need them.
              </p>
            </div>
            <div className="w-full lg:max-w-80">
              <Input
                size="sm"
                placeholder="Search briefs..."
                value={query}
                onChange={setQuery}
                icon={SearchLg}
                className="min-w-xs"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-container mx-auto flex min-h-[calc(80vh)] w-full flex-1 flex-col gap-6 px-4 py-10 lg:px-8 lg:py-12">
        {filteredBriefs.length > 0 ? (
          // Capped at 3 columns (playlists run 4-wide) so the briefs index
          // reads distinctly at a glance — wider cards, calmer rhythm.
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
            {/* Animation for filtered-out cards away smoothly to their new grid positions. */}
            <AnimatePresence mode="popLayout" initial={false}>
              {filteredBriefs.map((brief) => (
                <motion.div
                  key={brief.id}
                  layout
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <CollabCard brief={brief} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
            <p className="text-primary text-lg font-semibold">
              {query ? "No briefs match your search" : "No briefs yet"}
            </p>
            <p className="text-tertiary text-sm">
              {query
                ? "Try a different keyword."
                : "Check back soon for new community playlists."}
            </p>
          </div>
        )}
      </div>
    </AuthedShell>
  )
}
